// Wire format types for workspaces. MUST match camelCase serialization from
// apps/nexus/nexus/routes/workspaces.rs and openapi.rs registrations.
//
// All `id`, `userId`, `workspaceId`, `addedBy`, `invitedBy` fields are UUID
// strings (lowercase hyphenated; serde Uuid default). They are NOT display
// names — UI surfaces that show "invited by Bruno" need to resolve the UUID
// to a member via `listMembers()` data.

export type WorkspaceType = 'personal' | 'team'
export type WorkspaceRole = 'owner' | 'admin' | 'member'

// Narrower role for invitations: ownership is transferred via a dedicated
// endpoint, never via invitation. The server only ever sets `permission` to
// `'admin'` or `'member'` for workspace invitations. Keeping this narrow
// catches accidental `'owner'` assignments at the type layer.
export type InvitationRole = 'admin' | 'member'

export type MemberDefaultPermission = 'admin' | 'edit' | 'read'

export interface WorkspaceInfo {
  id: string                     // UUID
  name: string
  type: WorkspaceType
  role: WorkspaceRole            // the calling user's role in THIS workspace
  memberDefaultPermission: MemberDefaultPermission
  /**
   * When true, new signups whose email domain matches one of this
   * workspace's allowed-domain rows are auto-added as 'member' at signup.
   * Defaults to false. Toggled from the workspace settings → Domains tab.
   */
  autoJoinEnabled: boolean
}

export interface WorkspaceMember {
  userId: string                 // UUID
  email: string
  name: string | null
  avatarUrl: string | null
  role: WorkspaceRole
}

export interface AllowedDomain {
  workspaceId: string            // UUID
  domain: string
  addedBy: string                // UUID — the admin who added this domain
  addedAt: string                // ISO 8601 timestamp
}

export interface PendingInvitation {
  id: string                     // UUID
  email: string | null           // null for link-variant invitations
  role: InvitationRole           // 'admin' | 'member' only — never 'owner'
  expiresAt: string | null
  maxUses: number | null
  usesCount: number
  invitedBy: string              // UUID — the admin who created this invite
  createdAt: string              // ISO 8601 timestamp
}

// Helper: is this invitation an email invite or a shareable link?
export function isLinkInvitation(inv: PendingInvitation): boolean {
  return inv.email === null
}
